import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
} from "@packages/flow-core/module";
import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import Divider from "../Divider";
import styles from "./index.less";
import RenderInput from "../render/RenderInput";
import { useFlow } from "../FlowEdit/useFlow";
import RenderOutput from "../render/RenderOutput";
import { memo, useRef, useState } from "react";
import {
  Button,
  Form,
  FormInstance,
  Input,
  Modal,
  Space,
  Table,
  message,
} from "antd";
import { RenderInputProps } from "../render/RenderInput/types";
import { PlusOutlined } from "@ant-design/icons";
import { FlowNodeOutputItemType } from "@packages/flow-core/module/node/types";
import { ReactComponent as EditIcon } from "../../../assets/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.svg";
import classNames from "classnames";

type FormData = {
  key: string;
  description: string;
};
const ExtractKeyModal = ({
  onClose,
  onConfirm,
  visible,
  data,
}: {
  onClose: () => void;
  onConfirm: (data: FormData) => void;
  visible: boolean;
  data?: FormData;
}) => {
  const ref = useRef<FormInstance<FormData>>(null);

  async function handleConfirm() {
    const values = await ref.current?.validateFields();
    onConfirm(values!);
  }

  return (
    <Modal
      open={visible}
      title="属性字段配置"
      okText="确认"
      cancelText="取消"
      onOk={handleConfirm}
      afterClose={() => ref.current?.resetFields()}
      onCancel={onClose}
      destroyOnClose
    >
      <Form ref={ref} initialValues={data}>
        <Form.Item label="字段KEY" name="key" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="字段描述" name="description">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ExtractKeys = memo(({ item, moduleId }: RenderInputProps) => {
  const [open, setOpen] = useState(false);
  const {
    updateNodeValue,
    addModuleOutput,
    deleteModuleOutput,
    updateModuleOutput,
  } = useFlow();
  const dataRef = useRef<FormData>();
  return (
    <>
      <Table
        pagination={false}
        dataSource={item.value as { key: string; description: string }[]}
        className={classNames("pro-table", styles.table)}
        locale={{ emptyText: "暂无数据" }}
        size="small"
        bordered
        columns={[
          { title: "客户属性 KEY", dataIndex: "key" },
          { title: "客户属性描述", dataIndex: "description" },
          {
            render(_v, data) {
              return (
                <Space size="small">
                  <EditIcon
                    onClick={() => {
                      setOpen(true);
                      dataRef.current = data;
                    }}
                  />
                  <DeleteIcon
                    className={styles.danger}
                    onClick={() => {
                      const newValue = (item.value as FormData[]).filter(
                        (item) => item.key !== data.key
                      );
                      updateNodeValue(moduleId, item.key, newValue);
                      deleteModuleOutput(moduleId, data.key);
                    }}
                  />
                </Space>
              );
            },
          },
        ]}
      />
      <Button
        icon={<PlusOutlined />}
        size="small"
        onClick={() => {
          dataRef.current = undefined;
          setOpen(true);
        }}
        block
        className={styles["add-row-btn"]}
      >
        获取客户属性
      </Button>
      <ExtractKeyModal
        visible={open}
        onClose={() => setOpen(false)}
        onConfirm={(data) => {
          if (
            (dataRef.current && dataRef.current.key !== data.key) ||
            !dataRef.current
          ) {
            const existed = ((item.value as Array<FormData>) || []).find(
              (it) => it.key === data.key
            );
            if (existed) {
              message.warning("该key值已经存在");
              return;
            }
          }

          const outputNode: FlowNodeOutputItemType = {
            key: data.key,
            type: "source",
            valueType: "string",
            targets: [],
            label: `客户属性-${data.key}`,
            description: data.description,
            edit: true,
          };
          if (dataRef.current) {
            const index = (item.value as Array<FormData>).findIndex(
              (item) => item.key === dataRef.current!.key
            );
            const newValue = [...((item.value || []) as Array<FormData>)];
            newValue.splice(index, 1, data);
            updateNodeValue(moduleId, item.key, [...newValue]);
            updateModuleOutput(moduleId, dataRef.current.key, outputNode);
          } else {
            addModuleOutput(moduleId, outputNode);
            const newValue = ((item.value as Array<FormData>) || []).concat(
              data
            );
            updateNodeValue(moduleId, item.key, newValue);
          }
          setOpen(false);
        }}
        data={dataRef.current}
      />
    </>
  );
});

export default memo(function NodeGetAttributes({
  selected,
  data,
}: NodeProps<FlowModuleItemType>) {
  const { inputs, outputs, moduleId } = data;
  const { splitToolInputs } = useFlow();

  const { commonInputs } = splitToolInputs(inputs, moduleId);

  return (
    <NodeCard {...data} selected={selected}>
      <Divider text="输入" />
      <div className={styles.content}>
        <RenderInput
          flowInputList={commonInputs}
          moduleId={moduleId}
          CustomComponent={{ [ModuleInputKeyEnum.attributes]: ExtractKeys }}
        />
      </div>
      <Divider text="输出" />
      <div className={styles.content}>
        <RenderOutput moduleId={moduleId} flowOutputList={outputs} />
      </div>
    </NodeCard>
  );
});
